import { useEffect, useState } from "react";
import { api, brl } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Drawer, FieldText } from "@/pages/Clients";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  TrashIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function PartForm({ initial, onSave, onCancel }) {
  const [data, setData] = useState(
    initial || { code: "", name: "", quantity: 0, cost_price: 0, sale_price: 0, min_stock: 1, supplier: "" }
  );
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    await onSave({
      ...data,
      quantity: Number(data.quantity) || 0,
      cost_price: Number(data.cost_price) || 0,
      sale_price: Number(data.sale_price) || 0,
      min_stock: Number(data.min_stock) || 0,
    });
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="part-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldText label="Código *" value={data.code} onChange={set("code")} required testid="part-code-input" />
        <FieldText label="Nome *" value={data.name} onChange={set("name")} required testid="part-name-input" />
        <FieldText label="Quantidade" type="number" value={data.quantity} onChange={set("quantity")} testid="part-qty-input" />
        <FieldText label="Estoque mínimo" type="number" value={data.min_stock} onChange={set("min_stock")} testid="part-min-input" />
        <FieldText label="Preço de custo" type="number" step="0.01" value={data.cost_price} onChange={set("cost_price")} testid="part-cost-input" />
        <FieldText label="Preço de venda" type="number" step="0.01" value={data.sale_price} onChange={set("sale_price")} testid="part-sale-input" />
      </div>
      <FieldText label="Fornecedor" value={data.supplier} onChange={set("supplier")} testid="part-supplier-input" />
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 transition" data-testid="part-cancel-btn">
          Cancelar
        </button>
        <button type="submit" className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-[0_0_15px_rgba(37,99,235,0.4)] transition" data-testid="part-save-btn">
          Salvar
        </button>
      </div>
    </form>
  );
}

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/parts", { params: { q, low_only: lowOnly } });
    setItems(data);
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, lowOnly]);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/parts/${editing.id}`, data);
        toast.success("Peça atualizada");
      } else {
        await api.post("/parts", data);
        toast.success("Peça cadastrada");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erro ao salvar";
      toast.error(typeof msg === "string" ? msg : "Erro ao salvar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta peça?")) return;
    await api.delete(`/parts/${id}`);
    toast.success("Peça removida");
    load();
  };

  const movement = async (id, type) => {
    const qty = parseInt(window.prompt(`Quantidade de ${type}:`, "1"), 10);
    if (!qty || qty <= 0) return;
    try {
      await api.post("/parts/movement", { part_id: id, type, quantity: qty });
      toast.success("Movimento registrado");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erro";
      toast.error(typeof msg === "string" ? msg : "Erro");
    }
  };

  const lowCount = items.filter((p) => p.quantity <= p.min_stock).length;
  const totalValue = items.reduce((s, p) => s + (p.sale_price || 0) * (p.quantity || 0), 0);

  return (
    <div data-testid="inventory-page">
      <PageHeader
        title="Estoque"
        subtitle="Cadastro de peças, controle de entrada/saída e alertas de estoque baixo."
        actions={
          <button
            onClick={() => { setEditing(null); setOpen(true); }}
            data-testid="new-part-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 shadow-[0_0_15px_rgba(37,99,235,0.4)] transition hover:-translate-y-0.5"
          >
            <PlusIcon size={16} weight="bold" /> Nova peça
          </button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="glass rounded-xl p-5">
          <div className="text-xs uppercase tracking-wider text-slate-400">Itens cadastrados</div>
          <div className="font-display text-2xl font-bold mt-1">{items.length}</div>
        </div>
        <div className="glass rounded-xl p-5">
          <div className="text-xs uppercase tracking-wider text-slate-400">Valor em estoque (venda)</div>
          <div className="font-display text-2xl font-bold text-emerald-400 mt-1">{brl(totalValue)}</div>
        </div>
        <div className={`glass rounded-xl p-5 ${lowCount > 0 ? "border-rose-500/30" : ""}`}>
          <div className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1">
            <WarningCircleIcon size={14} weight="duotone" /> Estoque baixo
          </div>
          <div className={`font-display text-2xl font-bold mt-1 ${lowCount > 0 ? "text-rose-400" : ""}`}>{lowCount}</div>
        </div>
      </div>

      <div className="glass rounded-xl p-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <MagnifyingGlassIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="parts-search"
              placeholder="Buscar por nome ou código..."
              className="w-full bg-black/20 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-400 px-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={lowOnly}
              onChange={(e) => setLowOnly(e.target.checked)}
              data-testid="parts-low-filter"
              className="accent-blue-500"
            />
            Somente estoque baixo
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="parts-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <tr>
                <th className="py-3 pr-4">Código</th>
                <th className="py-3 pr-4">Nome</th>
                <th className="py-3 pr-4">Quantidade</th>
                <th className="py-3 pr-4">Custo</th>
                <th className="py-3 pr-4">Venda</th>
                <th className="py-3 pr-4">Margem</th>
                <th className="py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const low = p.quantity <= p.min_stock;
                const margin = p.cost_price > 0 ? ((p.sale_price - p.cost_price) / p.cost_price) * 100 : 0;
                return (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition" data-testid={`part-row-${p.id}`}>
                    <td className="py-3 pr-4 font-mono text-blue-400">{p.code}</td>
                    <td className="py-3 pr-4 font-medium">{p.name}</td>
                    <td className="py-3 pr-4">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-xs border ${low ? "bg-rose-500/10 text-rose-300 border-rose-500/30" : "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"}`}>
                        {p.quantity} un.
                      </span>
                      <span className="text-xs text-slate-500 ml-2">min {p.min_stock}</span>
                    </td>
                    <td className="py-3 pr-4 text-slate-300">{brl(p.cost_price)}</td>
                    <td className="py-3 pr-4 font-medium text-emerald-400">{brl(p.sale_price)}</td>
                    <td className="py-3 pr-4 text-slate-300">{margin.toFixed(0)}%</td>
                    <td className="py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => movement(p.id, "entrada")} className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-emerald-400 transition" title="Entrada" data-testid={`part-in-${p.id}`}>
                          <ArrowDownIcon size={16} />
                        </button>
                        <button onClick={() => movement(p.id, "saida")} className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-amber-400 transition" title="Saída" data-testid={`part-out-${p.id}`}>
                          <ArrowUpIcon size={16} />
                        </button>
                        <button onClick={() => { setEditing(p); setOpen(true); }} className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-blue-400 transition" data-testid={`edit-part-${p.id}`}>
                          <PencilSimpleIcon size={16} />
                        </button>
                        <button onClick={() => remove(p.id)} className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-rose-400 transition" data-testid={`delete-part-${p.id}`}>
                          <TrashIcon size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-slate-500">Nenhuma peça cadastrada.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer
        open={open}
        title={editing ? "Editar peça" : "Nova peça"}
        onClose={() => { setOpen(false); setEditing(null); }}
      >
        <PartForm initial={editing} onSave={save} onCancel={() => { setOpen(false); setEditing(null); }} />
      </Drawer>
    </div>
  );
}
